import { Injectable } from '@angular/core';

import { Period } from '../../../../../chat-common/src/lib/models/period';
import { DatePipe } from '@angular/common';
import { ChatOption } from '../../../../../chat-common/src/lib/models/chat.option';
import { GptService } from './gpt.service';
import { ChatMessage } from '../models/chat-message';
import { PeriodService } from '../../../../../chat-common/src/lib/services/period.service';
import { EventService } from '../../../../../chat-common/src/lib/services/event.service';
import { PdfReportService } from '../../../../../chat-common/src/lib/services/pdfreport.service';
import { ChatDataService } from 'projects/chat-common/src/lib/services/chat.data.service';
import { SleepService } from 'projects/chat-common/src/lib/services/sleep.service';
import { UserSegmentSleepState } from 'projects/chat-common/src/lib/models/sleep.segment';
import * as moment from 'moment';


export interface ParsedAction {
  base: string;    // npr. "logSymptom"
  param?: string;  // npr. "123" ili "2025-08-23"
}

@Injectable({
  providedIn: 'root'
})
export class ActionService {
  constructor(
    private periodService: PeriodService,
    private datePipe: DatePipe,
    private chatService: GptService,
    private eventService: EventService,
    private pdfReportService: PdfReportService,
    private chatDataService: ChatDataService,
    private sleepService: SleepService,
  ) { }


  isOverlayAction(option: ChatOption): boolean {
    switch (option.action) {
      case 'symptoms':
        return true;
      case 'showQuickView':
        return true;
      default:
        return false;
    }
  }

  graphAction(option: ChatOption): 'steps' | 'activity' | 'sleep' | null {
    switch (option.action) {
      case 'showSteps':
        return 'steps';
      case 'showSleep':
        return 'sleep';
      case 'showActivity':
        return 'activity';
      default:
        return null;
    }
  }

  shouldShowLoader(option: ChatOption): boolean {
    switch (option.action) {
      case 'analyseMyData':
        return true;

      case 'fertilityPrediction':
        return true;

      case 'pregnancyOverview':
        return true;

      case 'cycleAnalysis':
        return true;

      case 'analyseSleep':
        return true;

      case 'showHotTopics':
        return true;

      case 'showTopics':
        return true;

      case 'unknown':
        return true;

      default:
        return false;
    }
  }

  shouldShowMockResponse(action: string): boolean {
    switch (action) {
      case 'logSymptom':
        return true;

      case 'logMood':
        return true;

      case 'logSex':
        return true;

      case 'logDischarge':
        return true;

      case 'logFlow':
        return true;

      case 'logMedication':
        return true;

      case 'logSupplement':
        return true;

      case 'logHabit':
        return true;

      case 'logOvulation':
        return true;

      case 'startPeriod':
        return false;

      case 'stopPeriod':
        return false;

      case 'downloadReport':
        return true;

      default:
        return false;
    }
  }

  async handleOptions(options: ChatOption[]) {
    // TODO: Think about smarter way to handle multiple symptoms etc.
    for (const option of options) {
      await this.handleOption(option);
    }
  }

  shouldSkipSendingMessage(option: ChatOption): boolean {
    switch (option.action) {
      case 'fertilityPrediction':
        return true;
      case 'pregnancyOverview':
        return true;
      case 'cycleAnalysis':
        return true;
      case 'analyseSleep':
        return true;
      case 'unknown':
        return true;
      case 'analyseMyData':
        return true;
      case 'pass':
        return true;
      case 'showTopics':
        return true;
      case 'showHotTopics':
        return true;
      default:
        return false;
    }
  }

  async handleOption(option: ChatOption): Promise<void> {
    if (this.isOverlayAction(option)) return;

    let shouldGenerateUserDataMessage = false;
    const parsed = this.parseAction(option.action);
    switch (parsed.base) {
      case 'logSymptom':
        if (parsed.param != undefined) {
          await this.eventService.logSymptom(parsed.param);
          shouldGenerateUserDataMessage = true;
        }
        console.log('Logging symptom', parsed.param);
        break;

      case 'logMood':
        if (parsed.param != undefined) {
          await this.eventService.logMood(parsed.param);
          shouldGenerateUserDataMessage = true;
        }
        console.log('Logging mood', parsed.param);
        break;

      case 'logSex':
        if (parsed.param != undefined) {
          await this.eventService.logSex(parsed.param);
          shouldGenerateUserDataMessage = true;
        }
        console.log('Logging sex', parsed.param);
        break;

      case 'logDischarge':
        if (parsed.param != undefined) {
          await this.eventService.logDischarge(parsed.param);
          shouldGenerateUserDataMessage = true;
        }
        console.log('Logging discharge', parsed.param);
        break;

      case 'logFlow':
        if (parsed.param != undefined) {
          await this.eventService.logFlow(parsed.param);
          shouldGenerateUserDataMessage = true;
        }
        console.log('Logging flow', parsed.param);
        break;

      case 'logMedication':
        if (parsed.param != undefined) {
          await this.eventService.logMedication(parsed.param);
          shouldGenerateUserDataMessage = true;
        }
        console.log('Logging medication', parsed.param);
        break;

      case 'logContraception':
        if (parsed.param != undefined) {
          await this.eventService.logContraception(parsed.param);
          shouldGenerateUserDataMessage = true;
        }
        console.log('Logging contraception', parsed.param);
        break;

      case 'logSupplement':
        if (parsed.param != undefined) {
          await this.eventService.logSupplements(parsed.param);
          shouldGenerateUserDataMessage = true;
        }
        console.log('Logging supplement', parsed.param);
        break;

      case "logSleep":
        if (parsed.param !== undefined) {
          const sleepData = this.parseSleepParam(parsed.param);
          await this.sleepService.saveSleepSegment(
            sleepData.start,
            sleepData.end,
            sleepData.hours,
            UserSegmentSleepState.sleep,
          );
          shouldGenerateUserDataMessage = true;
        }
        console.log("Logging sleep", parsed.param);
        break;

      case 'logWeight':
        if (parsed.param !== undefined && parsed.param !== null) {
          const weight = Number(parsed.param);

          if (!isNaN(weight)) {
            await this.eventService.saveUserWeight(new Date(), weight);
            shouldGenerateUserDataMessage = true;
          }
        }
        console.log('Logging weight', parsed.param);
        break;
      case 'logTemperature':
        if (parsed.param !== undefined && parsed.param !== null) {
          const temperature = Number(parsed.param);

          if (!isNaN(temperature)) {
            await this.eventService.saveUserTemperature(new Date(), temperature);
            shouldGenerateUserDataMessage = true;
          }
        }
        console.log('Logging weight', parsed.param);
        break;

      case 'logHabit':
        if (parsed.param != undefined) {
          await this.eventService.logDiet(parsed.param);
          shouldGenerateUserDataMessage = true;
        }
        console.log('Logging habit', parsed.param);
        break;
      case 'logDiet':
        if (parsed.param != undefined) {
          await this.eventService.logDiet(parsed.param);
          shouldGenerateUserDataMessage = true;
        }
        console.log('Logging habit', parsed.param);
        break;

      case 'logOvulation':
        console.log('Logging ovulation', parsed.param);
        break;

      case 'startPeriod':
        // Just in case GPT returns wrong format
        let startDate = parsed.param != undefined ? new Date(parsed.param) : new Date();

        let startString = this.datePipe.transform(startDate, 'yyyy-MM-dd');
        let period = new Period(startString, null);
        await this.periodService.addPeriod(period);
        shouldGenerateUserDataMessage = true;
        break;

      case 'stopPeriod':
        await this.periodService.stopPeriod();
        shouldGenerateUserDataMessage = true;
        break;

      case 'syncIVY':
        console.log('Syncing IVY...');
        break;

      case 'syncHealthKit':
        console.log('Syncing HealthKit...');
        break;

      case 'uploadDocument':
        console.log('Uploading document...');
        break;

      case 'purchaseIVY':
        this._purchaseIVY();
        console.log('Purchasing IVY...');
        break;

      case 'analyseMyData':
        await this.chatService.sendMessage(new ChatMessage(option.display));
        console.log('Analysing my data...');
        break;

      case 'downloadReport':
        this.pdfReportService.triggerExportDataPdfReport();
        await this.chatService.sendAssistantMessage(ChatMessage.mockDownloadReportResponse());
        console.log('Download report...');
        break;

      case 'fertilityPrediction':
        await this.chatService.sendMessage(new ChatMessage("Please predict my next ovulation day and fertile window."));
        break;

      case 'pregnancyOverview':
        await this.chatService.sendMessage(new ChatMessage("Please give me quick overview of my pregnancy."));
        break;

      case 'cycleAnalysis':
        await this.chatService.sendMessage(new ChatMessage("Please give me quick overview of my cycles (regular, irregular, length etc)."));
        break;

      case 'analyseSleep':
        await this.chatService.sendMessage(new ChatMessage("Please analyse my sleep patterns."));
        break;

      case 'showHotTopics':
        await this.chatService.sendMessage(new ChatMessage(`Tell me more about ${option.display}`));
        break;

      case 'showTopics':
        if (parsed.param != undefined) {
          let parsedParams = this._parseTopicParams(parsed.param);
          await this.chatService.sendMessage(new ChatMessage(`${parsedParams}`));
        }
        break;

      case 'unknown':
        await this.chatService.sendMessage(new ChatMessage(option.display));
        break;

      case 'pass':
        console.log('Pass action')
        await this.chatService.sendAssistantMessage(ChatMessage.mockPassResponse());
        await this.chatService.sendMessage(new ChatMessage('#chatresponse: Continue logging/conversation'));
        break;

      default:
        console.warn('Unknown action:', option.action);
    }

    if (shouldGenerateUserDataMessage) {
      this.chatDataService.generateUserDataMessage();
    }
  }

  private _parseTopicParams(input: string): string {
    const actions = input
      .replace(/[{}]/g, "")
      .split(",")
      .map(a => a.trim());

    const firstAction = actions[0]?.toLowerCase();
    const secondAction = actions[1]?.toLowerCase();

    if (secondAction === "expertadvice") {
      return `Expert advice about ${firstAction === "cycle" ? "menstrual cycle" : firstAction}`;
    }

    switch (firstAction) {
      case "better sleep":
        return `Better sleep ${secondAction ?? ""}`;
      case "better sex":
        return `Better sex ${secondAction ?? ""}`;
      case "nutrition":
        return `Nutrition ${secondAction ?? ""}`;
      case "cycle":
        return `Cycle ${secondAction ?? ""}`;
      default:
        return `Show me ${firstAction} ${secondAction ?? ""}`;
    }
  }

  private parseAction(action: string): ParsedAction {
    const [base, param] = action.split('#');
    return { base: base, param: param?.toLowerCase() };
  }

  private _purchaseIVY(sameTab = false): void {
    const url = 'https://www.ivyhealth.ai/ivy-plus/';
    if (sameTab) {
      window.location.href = url;
    } else {
      const w = window.open(url, '_blank', 'noopener,noreferrer');
      if (w) w.opener = null;
    }
  }

  private parseSleepParam(param: string): { start: Date | null; end: Date | null; hours: number | null } {
    const clean = param.replace(/[{}]/g, "").trim();
    const parts = clean.split(",", 3).map(p => p.trim());

    if (parts.length !== 3) {
      return { start: null, end: null, hours: null };
    }

    const start = this.parseSleepDate(parts[0]);
    const end = this.parseSleepDate(parts[1]);
    const hours = this.parseSleepHours(parts[2]);

    return { start, end, hours };
  }

  private parseSleepDate(value: string): Date | null {
    if (!value) return null;

    const m = moment(value, "YYYY-MM-DD HH:mm", true);
    return m.isValid() ? m.toDate() : null;
  }

  private parseSleepHours(value: string): number | null {
    if (!value) return null;
    const n = Number(value);
    return isNaN(n) ? null : n;
  }
}
